// The manifest's lines. Four kinds of row in one table, on purpose.
//
// ── WHY NOT FOUR TABLES ──────────────────────────────────────────────────────
// A board with a record, a board too dead to have one, a count of loose stock
// and one sticker-tracked unit (GPS, LED) are the same thing to everybody
// downstream: something in a box, with a reason, on a slip. Splitting them would
// make "what is going back" a sum of four numbers and put the print list in four
// places.
//
// They are told apart three ways, and only one of them is color: the ID reads
// differently (a drone serial or an L-tag, a D-number, a Q-tag, a P-tag), the
// Kind column says so in words, and a left edge marks the row. Color
// reinforces, never carries.
//
// ── THE ID COLUMN IS NOT A DERIVATION ────────────────────────────────────────
// It renders line.label_identity verbatim, the string the server stored and the
// sticker carries. Formatting it here, even to pad or upper-case it, would break
// the one link this feature exists to provide.
//
// ── THE SECOND LINE IS HOW A ROW IS FOUND AGAIN ──────────────────────────────
// A board is found by its hardware UID. Loose stock and a tracked unit have
// none, so the receipt box barcode takes that place. The purchase order follows on both, because it is
// what the vendor files the return under. The box is our handle and shows only
// here; the printed slip leaves it off (see returnSlip.ts).
//
// ── THE VESSEL IS THE SHARED TABLE ───────────────────────────────────────────
// One table component per pattern, so this is `components/table/DataTable` and
// the page draws none of it: no wrapper box, no header band, no row rule, no
// minimum width. The three columns that hold controls (print, reason,
// designator) reach their handlers through the table's `meta`, because the
// columns array below is module scope and must never change identity — a fresh
// array hands flexRender a new component type and React remounts the cell,
// which throws the caret out of the designator field mid-word. See
// web/docs/features/shared-table.md.
//
// No sorting anywhere: a manifest is read in the order the lines were put on
// it, and a re-ordered manifest is a different slip from the one in the carton.

import React from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef, Table } from '@tanstack/react-table';
import ItemName from '../../components/ItemName';
import { IconButton } from '../../components/ui/Button';
import Tooltip from '../../components/ui/Tooltip';
import { IconClose } from '../../components/ui/icons';
import { Alert } from '../../components/ui/Alert';
import { Appear } from '../../components/ui/Appear';
import { EmptyState } from '../../components/ui/EmptyState';
import { Field } from '../../components/ui/Field';
import { Select } from '../../components/ui/Select';
import { DataTable } from '../../components/table/DataTable';
import TableCheckbox from '../../components/table/TableCheckbox';
import {
  isPartInstanceLine,
  isQuantityLine,
  reasonDisplay,
  type ManifestLine,
  type ReturnReason,
} from './boardReturnsApi';

interface Props {
  lines: ManifestLine[];
  reasons: ReturnReason[];
  /** Draft manifests are editable. A sent one only takes print-list changes. */
  editable: boolean;
  busyLineId: string | null;
  /** A select-all is mid-flight. The header checkbox has to go inert. */
  togglingAll: boolean;
  /**
   * Lines added since the last render, so they can announce themselves.
   *
   * The ONE charm moment on this surface, and it is feedback before it is charm: a
   * tech is looking at a tray of boards, not at the screen, and a row settling in
   * confirms the scan registered without asking them to look up.
   */
  landedIds: Set<string>;
  onUpdate: (lineId: string, patch: {
    reason_id?: number | null;
    reason_raw?: string | null;
    designator?: string | null;
    in_print_list?: boolean;
  }) => void;
  onDelete: (lineId: string) => void;
  onToggleAll: (next: boolean) => void;
}

/**
 * Everything a cell needs that changes while somebody is working: the write
 * handlers, which row has a request out, whether the document takes edits at
 * all, and the header checkbox's own state. It travels by `meta` rather than in
 * a closure inside a column, which is the shared table's first contract.
 *
 * NEVER wrap a cell renderer below in React.memo. tanstack memoizes the cell's
 * context object, so the render that picks up a new meta is the table's own — a
 * memoized cell skips it and paints the previous answer.
 */
interface LineTableMeta {
  editable: boolean;
  busyLineId: string | null;
  reasons: ReturnReason[];
  /** Every row is already on the print list, so the header toggle clears. */
  allPrinting: boolean;
  togglingAll: boolean;
  onUpdate: Props['onUpdate'];
  onDelete: Props['onDelete'];
  onToggleAll: Props['onToggleAll'];
}

/** The page always passes it, so the read side does not carry an undefined. */
const metaOf = (table: Table<ManifestLine>): LineTableMeta =>
  table.options.meta as LineTableMeta;

/**
 * Four edges, four kinds, and still only three hues. Stock takes the neutral
 * role: it is the ordinary one, and amber is spent on the row that needs a
 * look. A tracked unit is a unit on record like a board, so it shares the
 * board's gold and differs by pattern (dashed) rather than by a fourth hue, the
 * same solid-versus-broken idiom the printed slip uses. On the row rather than
 * on a cell, because the shared table draws no rules of its own and a tint that
 * stops at the first column reads as a decorated checkbox instead of a marked
 * line.
 */
const edgeTint = (line: ManifestLine): string => {
  if (line.doa_number !== null) return 'border-l-2 border-l-amber-500/70';
  if (isQuantityLine(line)) return 'border-l-2 border-l-on-surface-variant';
  if (isPartInstanceLine(line)) return 'border-l-2 border-dashed border-l-primary/50';
  return 'border-l-2 border-l-primary/50';
};

const LINE_COLUMNS: ColumnDef<ManifestLine, any>[] = [
  {
    id: 'print',
    meta: { align: 'center' },
    header: ({ table }) => {
      const { allPrinting, togglingAll, onToggleAll } = metaOf(table);
      return (
        <TableCheckbox
          checked={allPrinting}
          disabled={togglingAll}
          onChange={() => onToggleAll(!allPrinting)}
          aria-label={allPrinting ? 'Clear the print list' : 'Add every row to the print list'}
        />
      );
    },
    cell: ({ row, table }) => {
      const line = row.original;
      const { onUpdate } = metaOf(table);
      return (
        <TableCheckbox
          checked={line.in_print_list}
          onChange={() => onUpdate(line.id, { in_print_list: !line.in_print_list })}
          aria-label={`Print a label for ${line.label_identity}`}
        />
      );
    },
  },

  {
    id: 'line_number',
    header: '#',
    meta: { align: 'right', numeric: true, cellClass: 'text-xs text-on-surface-variant' },
    cell: ({ row }) => row.original.line_number,
  },

  {
    // Verbatim. See the header comment.
    id: 'identity',
    header: 'ID on label',
    meta: { cellClass: 'font-mono text-sm text-on-surface whitespace-nowrap' },
    cell: ({ row }) => row.original.label_identity,
  },

  {
    id: 'kind',
    header: 'Kind',
    meta: { cellClass: 'text-xs whitespace-nowrap' },
    cell: ({ row }) => {
      const line = row.original;
      if (line.doa_number !== null) return <span className="text-amber-300">Never flashed</span>;
      if (isQuantityLine(line)) return <span className="text-on-surface">Stock</span>;
      if (isPartInstanceLine(line)) return <span className="text-on-surface-variant">Tracked unit</span>;
      return <span className="text-on-surface-variant">On record</span>;
    },
  },

  {
    id: 'board',
    header: 'Board',
    meta: {
      cellClass: 'text-xs text-on-surface-variant',
      // Per ROW, not per column: a stock row needs room for an item name and a
      // board row must not fold its type and serial onto two lines. cellAttrs
      // is the td-level hook for exactly that.
      cellAttrs: (line: ManifestLine) => ({
        className: isQuantityLine(line) || isPartInstanceLine(line)
          ? 'min-w-[180px]'
          : 'whitespace-nowrap',
      }),
    },
    cell: ({ row }) => {
      const line = row.original;
      const isStock = isQuantityLine(line);
      const isUnit = isPartInstanceLine(line);
      return (
        <>
          {isUnit ? (
            <div className="flex items-baseline gap-1.5">
              <span className="shrink-0 font-mono">{line.effective_board_type || '—'}</span>
              <ItemName
                item={{
                  base_name: line.base_name,
                  variant_detail: line.variant_detail,
                  display_name: line.display_name,
                  sku: line.sku,
                }}
                size="sm"
                inline
                className="min-w-0"
              />
            </div>
          ) : isStock ? (
            <div className="flex items-baseline gap-1.5">
              {/* The count leads, because on a stock row it is the fact the
                  vendor's desk counts against. */}
              <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums text-on-surface">
                {line.quantity} ×
              </span>
              {/* The four name fields explicitly, not the whole line: ItemName's
                  fallback chain reaches for description columns too, and a
                  manifest line has none of them. */}
              <ItemName
                item={{
                  base_name: line.base_name,
                  variant_detail: line.variant_detail,
                  display_name: line.display_name,
                  sku: line.sku,
                }}
                size="sm"
                inline
                className="min-w-0"
              />
            </div>
          ) : (
            <>
              <span className="font-mono">{line.effective_board_type || '—'}</span>
              {line.effective_model_serial && (
                <span className="text-on-surface-variant"> · {line.effective_model_serial}</span>
              )}
            </>
          )}
          {/* The hardware UID is the vendor's own handle on the unit, so it stays
              visible here even though the label cannot hold it. A stock row has
              none, so the box it came out of takes that place. Either way the
              purchase order follows: it is what the vendor files the return
              under, on a board as much as on a count of parts. */}
          {isUnit && line.instance_installed_on && (
            <div className="text-[11px]">
              Fitted to{' '}
              <Link
                to={`/drone/${line.instance_installed_on}`}
                className="font-mono text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-sm"
              >
                {line.instance_installed_on}
              </Link>
            </div>
          )}
          {isStock || isUnit ? (
            (line.box_barcode || line.po_number) && (
              <div className="text-[11px] text-on-surface-variant font-mono">
                {[line.box_barcode, line.po_number].filter(Boolean).join(' · ')}
              </div>
            )
          ) : (
            (line.primary_uid || line.po_number) && (
              <div className="text-[11px] text-on-surface-variant font-mono">
                {[line.primary_uid, line.po_number].filter(Boolean).join(' · ')}
              </div>
            )
          )}
          {line.po_is_other_vendor && (
            <div className="text-[11px] text-amber-300">
              Ordered from a different vendor, so the slip leaves this order off.
            </div>
          )}
        </>
      );
    },
  },

  {
    id: 'reason',
    header: 'Reason',
    meta: { cellClass: 'min-w-[200px]' },
    cell: ({ row, table }) => {
      const line = row.original;
      const { editable, reasons, onUpdate } = metaOf(table);
      return (
        <>
          {editable ? (
            <Select
              label={`Reason for ${line.label_identity}`}
              size="sm"
              value={line.reason_id ? String(line.reason_id) : ''}
              placeholder={line.reason_raw ? `Needs a code: ${line.reason_raw}` : 'No reason yet'}
              onChange={(key) => onUpdate(line.id, { reason_id: key ? Number(key) : null })}
              options={[
                { key: '', label: line.reason_raw ? `Needs a code: ${line.reason_raw}` : 'No reason yet' },
                ...reasons.map((r) => ({ key: String(r.id), label: r.label })),
              ]}
            />
          ) : (
            <span className="text-sm text-on-surface">{reasonDisplay(line)}</span>
          )}
          {/* The typed words stay visible next to the code they were classified
              as, so a wrong classification is obvious. */}
          {line.reason_raw && line.reason_id && (
            <div className="mt-1 text-[11px] text-on-surface-variant line-clamp-2">
              {line.reason_raw}
              {line.reason_origin === 'ai' && (
                <span className="ml-1 text-primary/70">coded by reason pass</span>
              )}
            </div>
          )}
        </>
      );
    },
  },

  {
    id: 'designator',
    header: 'Designator',
    meta: { cellClass: 'w-32' },
    cell: ({ row, table }) => {
      const line = row.original;
      const { editable, busyLineId, onUpdate } = metaOf(table);
      if (!editable) {
        return <span className="text-sm text-on-surface-variant">{line.designator || '—'}</span>;
      }
      return (
        <Field
          size="sm"
          defaultValue={line.designator || ''}
          maxLength={40}
          placeholder="U7"
          disabled={busyLineId === line.id}
          aria-label={`Designator for ${line.label_identity}`}
          // On blur, not on change: this is a text field in a table row and a
          // PATCH per keystroke would be forty requests to type "connector".
          // Uncontrolled, so it survives a re-render only while the row keeps
          // its identity — which is what getRowId below is for.
          onBlur={(e) => {
            const next = e.target.value.trim() || null;
            if (next !== (line.designator || null)) {
              onUpdate(line.id, { designator: next });
            }
          }}
        />
      );
    },
  },

  {
    // The column stays on a sent manifest and renders nothing there, rather
    // than coming and going: a header over a cell that is not there is how a
    // row ends up one column short at its right edge.
    id: 'remove',
    header: '',
    meta: { align: 'center' },
    cell: ({ row, table }) => {
      const line = row.original;
      const { editable, busyLineId, onDelete } = metaOf(table);
      if (!editable) return null;
      return (
        <Tooltip label={`Remove ${line.label_identity} from the manifest`}>
          <IconButton
            size="sm"
            label={`Remove ${line.label_identity}`}
            onClick={() => onDelete(line.id)}
            disabled={busyLineId === line.id}
          >
            <IconClose />
          </IconButton>
        </Tooltip>
      );
    },
  },
];

const LineTable: React.FC<Props> = ({
  lines,
  reasons,
  editable,
  busyLineId,
  togglingAll,
  landedIds,
  onUpdate,
  onDelete,
  onToggleAll,
}) => {
  const printCount = lines.filter((l) => l.in_print_list).length;
  const allPrinting = printCount === lines.length;
  const stockLines = lines.filter(isQuantityLine);
  const units = stockLines.reduce((sum, l) => sum + (l.quantity || 0), 0);
  const needReason = lines.filter((l) => !l.reason_id).length;

  return (
    <DataTable<ManifestLine>
      data={lines}
      columns={LINE_COLUMNS}
      // Identity, not position: the designator input is uncontrolled and the
      // landing animation is keyed off the row, so both survive a refetch only
      // while a line keeps the same row id.
      getRowId={(line) => line.id}
      meta={{
        editable,
        busyLineId,
        reasons,
        allPrinting,
        togglingAll,
        onUpdate,
        onDelete,
        onToggleAll,
      }}
      // A prop, not a column: these three facts are about the whole row, and
      // closing over page state here is exactly what rowProps is for. The
      // landing set has to reach the row this way — rowProps is handed the row
      // and its index, never the table, so it cannot read `meta`.
      rowProps={(line) => ({
        className: [
          edgeTint(line),
          busyLineId === line.id ? 'opacity-50' : '',
          landedIds.has(line.id) ? 'motion-safe:animate-rowLand' : '',
        ]
          .filter(Boolean)
          .join(' '),
      })}
      // 'replace', so an empty manifest is the sentence and nothing else. The
      // row default would leave eight column headings standing over a table
      // with no table in it.
      emptyMode="replace"
      empty={
        <EmptyState variant="area" title="Nothing on this manifest yet.">
          Scan a board or a GPS or LED sticker above, or add rows for boards that never
          flashed. Stock lines arrive from a purchase order.
        </EmptyState>
      }
      // The prose band, not per-column footers: none of these figures belongs
      // under a column, and the warning is a sentence. Withheld while the
      // manifest is empty, where every count would be a zero stated as a fact.
      footer={
        lines.length === 0 ? undefined : (
          // The band itself is the component's; this node is here for the wrap.
          // Five items in one row is a horizontal scrollbar on a phone, and the
          // band does not wrap on its own.
          <div className="grow min-w-0 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-on-surface-variant">
            {/* The line count leads, because it is the number somebody reads back
                on a call with the vendor. It says "boards" only while that is the
                whole truth: once loose stock is on the manifest, one line is not
                one board, and a count that says otherwise is the number people
                quote. */}
            <span className="text-on-surface">
              <b className="text-base font-semibold tabular-nums">{lines.length}</b>{' '}
              {stockLines.length > 0
                ? `line${lines.length === 1 ? '' : 's'}`
                : `board${lines.length === 1 ? '' : 's'}`}
            </span>
            <Appear show={stockLines.length > 0}>
              <b className="text-on-surface tabular-nums">{units}</b>{' '}
              unit{units === 1 ? '' : 's'} of stock
            </Appear>
            <span>
              <b className="text-on-surface tabular-nums">
                {lines.filter((l) => l.doa_number !== null).length}
              </b>{' '}
              never flashed
            </span>
            <span>
              <b className="text-on-surface tabular-nums">{printCount}</b> in the print list
            </span>
            {/* Not one more item in a list of counts: this is the thing that
                BLOCKS sending, so it reads as a warning and says what to do. */}
            <Appear as="div" show={needReason > 0} className="ml-auto">
              <Alert tone="warning" variant="text" live={false} className="!w-auto !text-xs !leading-4">
                <b className="tabular-nums">{needReason}</b>{' '}
                need a reason before this can be sent
              </Alert>
            </Appear>
          </div>
        )
      }
    />
  );
};

export default LineTable;
